/**
 * UsageSummaryModal.jsx — bulk fund × feature usage matrix.
 *
 * Pick a date window, get a grid: one row per fund (sub-funds rolled into
 * their parent), one column per feature bucket (same taxonomy as the raw
 * usage export), with per-fund totals, per-feature totals, and a grand
 * total. "Copy for Sheets" puts the grid on the clipboard as TSV so it
 * pastes straight into a spreadsheet; Download CSV saves the same table.
 *
 * Props:
 *   getToken — auth
 *   onClose  — () => void
 */

import { useState } from 'react';
import { fetchUsageSummary } from '../api/backend.js';
import { downloadCsv } from '../utils/csv.js';

const _dateStr = (d) => d.toISOString().slice(0, 10);
const _fmt = (n) => (n ? n.toLocaleString() : '—');

function buildGrid(data) {
  // Shared row-major table used by both Copy (TSV) and Download (CSV).
  const header = ['Fund', 'Active Users', ...data.features, 'Total'];
  const body = data.rows.map((r) => [
    r.fund_name,
    r.users,
    ...data.features.map((f) => r.counts[f] ?? 0),
    r.total,
  ]);
  const totals = [
    'Total',
    data.total_users,
    ...data.features.map((f) => data.feature_totals[f] ?? 0),
    data.grand_total,
  ];
  return { header, body, totals };
}

export default function UsageSummaryModal({ getToken, onClose }) {
  const [start, setStart] = useState(() => _dateStr(new Date(Date.now() - 29 * 86_400_000)));
  const [end, setEnd] = useState(() => _dateStr(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const rangeValid = start && end && start <= end;

  async function load() {
    if (!rangeValid || busy) return;
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      setData(await fetchUsageSummary(getToken, { start, end }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function copyTsv() {
    if (!data) return;
    const { header, body, totals } = buildGrid(data);
    const tsv = [header, ...body, totals].map((row) => row.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      setError(`Clipboard copy failed: ${e.message}`);
    }
  }

  function exportCsv() {
    if (!data) return;
    const { header, body, totals } = buildGrid(data);
    const rows = [...body, totals].map((row) => Object.fromEntries(header.map((h, i) => [h, row[i]])));
    downloadCsv(`usage_summary_${data.start}_to_${data.end}.csv`, rows);
  }

  const th = { padding: '6px 10px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.3px', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)' };
  const td = { padding: '5px 10px', fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', fontVariantNumeric: 'tabular-nums' };

  return (
    <div className="merge-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="merge-modal" style={{ width: 'min(1320px, 96vw)' }}>
        <div className="merge-modal__title">Customer usage summary</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
            style={{ fontSize: 13, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
          />
          <span style={{ color: 'var(--text-3)', fontSize: 13 }}>to</span>
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            style={{ fontSize: 13, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
          />
          <button className="btn btn--primary btn--sm" onClick={load} disabled={!rangeValid || busy}>
            {busy ? 'Loading…' : 'Load'}
          </button>
          <div style={{ flex: 1 }} />
          {data && (
            <>
              <button className="btn btn--ghost btn--sm" onClick={copyTsv}>{copied ? '✓ Copied' : 'Copy for Sheets'}</button>
              <button className="btn btn--ghost btn--sm" onClick={exportCsv}>Download CSV</button>
            </>
          )}
        </div>

        {error && <div style={{ color: 'var(--c-red)', fontSize: 12.5 }}>{error}</div>}

        {data && (
          <div style={{ overflow: 'auto', maxHeight: '66vh', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Fund</th>
                  <th style={th}>Active Users</th>
                  {data.features.map((f) => <th key={f} style={th}>{f}</th>)}
                  <th style={th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.fund_id ?? '__unlinked__'} style={r.fund_id === null ? { color: 'var(--text-3)' } : undefined}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.fund_name}</td>
                    <td style={td}>{_fmt(r.users)}</td>
                    {data.features.map((f) => <td key={f} style={td}>{_fmt(r.counts[f])}</td>)}
                    <td style={{ ...td, fontWeight: 700 }}>{_fmt(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ ...td, textAlign: 'left', borderTop: '2px solid var(--border)' }}>Total</td>
                  <td style={{ ...td, borderTop: '2px solid var(--border)' }}>{_fmt(data.total_users)}</td>
                  {data.features.map((f) => (
                    <td key={f} style={{ ...td, borderTop: '2px solid var(--border)' }}>{_fmt(data.feature_totals[f])}</td>
                  ))}
                  <td style={{ ...td, borderTop: '2px solid var(--border)' }}>{_fmt(data.grand_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {!data && !error && (
          <div className="merge-modal__note">
            Event counts per feature for every fund with linked usage in the window — sub-funds
            roll up into their parent, and external users no contact matches land in
            "(unlinked users)". Internal (lumosity/implied) and test accounts are excluded.
            Same feature buckets as the raw usage export.
          </div>
        )}

        <div className="merge-modal__footer">
          <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Close</button>
        </div>
      </div>
    </div>
  );
}
